import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { PencilIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * A heading you can rename in place.
 *
 * One component for all four things that carry a name — conversations,
 * reports, dashboards and tiles — because they are the same interaction over
 * four `PATCH` endpoints that already existed, and four hand-rolled copies
 * would be four chances to get the keyboard contract subtly different.
 *
 * The rules it keeps:
 *
 * - **Escape cancels, Enter commits, blur commits.** Blur-commits because the
 *   most common way to leave a one-line field is to click somewhere else, and
 *   losing the edit at that point reads as the app throwing your work away.
 * - **An empty name cancels rather than saving.** Every one of these has a
 *   fallback label for the unnamed case, so a blank save is indistinguishable
 *   from never having renamed it — and it is much more often a slip than an
 *   intent.
 * - **A failed save keeps you in the editor, with your text.** The mutation
 *   cache already says *why* (backlog item 11); what it cannot do is give the
 *   sentence back to you. Dropping to display mode on failure would show the
 *   old name beside a toast about the new one.
 * - **The trigger is a real button, not a click handler on the heading.** It
 *   has to be reachable and announceable without a mouse (PRD NFR-5), and the
 *   heading itself still has to be selectable text.
 */

export function EditableTitle({
  value,
  onSave,
  placeholder,
  label,
  as: Tag = "h1",
  className,
  inputClassName,
}: {
  value: string;
  /** Rejecting keeps the editor open with the text still in it. */
  onSave: (next: string) => Promise<unknown>;
  /** Shown when there is no name yet; never saved as one. */
  placeholder: string;
  /** What is being renamed, for the button's accessible name. */
  label: string;
  as?: "h1" | "h2" | "h3";
  className?: string;
  inputClassName?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  // A rename that lands from somewhere else — another tab, a refetch — should
  // be visible here. Only while not editing: adopting it mid-edit would
  // overwrite what is being typed.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) input.current?.select();
  }, [editing]);

  const commit = async () => {
    const next = draft.trim();
    if (!next || next === value) {
      setEditing(false);
      setDraft(value);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      // Stay open, keep the text. The toast carries the reason.
      input.current?.focus();
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void commit();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setDraft(value);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={input}
        value={draft}
        disabled={saving}
        aria-label={`Rename ${label}`}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => void commit()}
        className={cn(
          "bg-card text-ink border-accent rounded-element w-full min-w-0 border px-2 py-0.5",
          "outline-none disabled:opacity-60",
          className,
          inputClassName,
        )}
      />
    );
  }

  return (
    <div className="group/title flex min-w-0 items-center gap-1.5">
      <Tag className={cn("min-w-0 truncate", className)}>{value || placeholder}</Tag>
      <button
        type="button"
        aria-label={`Rename ${label}`}
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
        className={cn(
          "text-ink-disabled hover:text-ink hover:bg-hover rounded-inner motion-fast",
          "inline-flex size-6 shrink-0 items-center justify-center transition-opacity",
          // Quiet until wanted, but never hidden from the keyboard: it stays
          // focusable and reveals itself on focus.
          "opacity-0 group-hover/title:opacity-100 focus-visible:opacity-100",
        )}
      >
        <PencilIcon className="size-3.5" />
      </button>
    </div>
  );
}
